import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'ui';
import FormElementWrapper from '../components/FormElementWrapper';
import InlineEdit from '../components/InlineEdit';
import useGetFormGeneratorItemError from '../hooks/useGetFormGeneratorItemError';

const Signature = props => {
  const [padWidth, setPadWidth] = useState(400);
  const ref = useRef(null);
  const containerRef = useRef(null);
  const { data, onChange, disabled, isBuilder, value, readOnly } = props;
  const { requiredError } = useGetFormGeneratorItemError(data.id);
  const [isEditing, setIsEditing] = useState(false);
  const { setFieldValue, values } = useFormikContext();
  const index = values?.data?.findIndex(({ id }) => id === props.data.id);

  const handleLabelChange = e => {
    const { name, value } = e.target;
    setFieldValue(`data[${index}].${name}`, value);
  };

  const handleClear = useCallback(() => {
    ref.current?.clear();
    onChange(data.type, data.id, '');
  }, [onChange, data]);

  const handleChange = useCallback(() => {
    onChange(data.type, data.id, ref.current.toDataURL());
  }, [onChange, data]);

  useEffect(() => {
    if (ref.current && value) {
      setTimeout(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = value;

        img.onload = () => {
          ref.current?.clear();
          const canvas = ref.current?.getCanvas();
          const ctx = canvas?.getContext('2d');
          ctx?.drawImage(img, 0, 0);
        };
      }, 100);
    }
  }, [value]);

  const handleUpdatePadWidth = useCallback(() => {
    if (!containerRef.current) {
      setPadWidth(400);
    } else {
      setPadWidth(containerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    handleUpdatePadWidth();
    window.addEventListener('resize', handleUpdatePadWidth);

    return () => {
      window.removeEventListener('resize', handleUpdatePadWidth);
    };
  }, [handleUpdatePadWidth]);

  const isValueLink = value && value?.toLowerCase()?.startsWith('http');
  return (
    <FormElementWrapper {...props} onClick={!readOnly ? () => setIsEditing(true) : undefined}>
      <div className="font-semibold">
        <InlineEdit
          value={data.label}
          onChange={handleLabelChange}
          fieldName="label"
          isEditing={isEditing}
          onSetIsEditing={setIsEditing}
          readOnly={readOnly}
        />
      </div>
      {isValueLink ? (
        <img src={value} alt="signature" />
      ) : (
        <div ref={containerRef} style={isBuilder || disabled ? { pointerEvents: 'none' } : {}}>
          <div className="!h-[250px] w-full">
            <SignatureCanvas
              ref={ref}
              canvasProps={{
                width: padWidth,
                height: 250,
                className: 'top-0 left-0 w-full !h-[250px]',
              }}
              onEnd={handleChange}
              onResize={handleClear}
            />
          </div>
        </div>
      )}
      <div className="text-neutral-500 text-[12px] italic mt-2">
        I acknowledge and agree that by proceeding with this electronic signature process, I am
        providing my express consent to electronically sign this document. I confirm that this
        electronic signature is intended to authenticate my identity and indicate my full acceptance
        of the terms and conditions contained within this document. I understand that this
        electronic signature is legally binding and equivalent to a handwritten signature under the
        Electronic Transactions Act 1999 (Cth) and all other applicable laws.
      </div>
      {!isBuilder && !disabled && !isValueLink && (
        <div className="w-fit print:hidden">
          <Button variant="plain" onClick={handleClear} className="w-auto mt-2" focusable={false}>
            Clear
          </Button>
        </div>
      )}
      {requiredError && <div style={{ color: 'red', marginTop: 4 }}>Signature is required</div>}
    </FormElementWrapper>
  );
};

export default Signature;
